import { type PropsWithChildren } from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import type {
  FormatterWithPrecision,
  DateTimeFormatter,
} from '../../core/types/formatter.js';
import { getFormatterByTicks } from '../../core/utils/formatter-by-ticks.js';
import { getTimeFormatter } from '../../core/utils/get-time-formatter.js';
import {
  getHorizontalNumTicks,
  getVerticalNumTicks,
} from '../../core/utils/ticks.js';
import { XYChartAxesFormatterContext } from '../context/axes/XYChartAxesFormatter.context.js';
import { useXYChartData } from '../hooks/useXYChartData.js';
import { useXYChartScales } from '../hooks/useXYChartScales.js';
import { isNumericalAxis, isTimeAxis } from '../types/guards.js';
import type { AxisId, AxisIdToFormatter } from '../types/xy-chart-internals.js';
import { getCurrentScale } from '../utils/get-scale.js';
import { isXAxis } from '../utils/is-axis.js';

export function XYChartAxesFormatterProvider({ children }: PropsWithChildren) {
  const { width, height } = useGridPlotSize();
  const { axes } = useXYChartData();
  const axisIdToScales = useXYChartScales();
  const axisIdToFormatter: AxisIdToFormatter = new Map<
    AxisId,
    FormatterWithPrecision | DateTimeFormatter
  >();

  const horizontalNumTicks = getHorizontalNumTicks(width);
  const verticalNumTicks = getVerticalNumTicks(height);

  for (let i = 0; i < axes.length; i++) {
    const axis = axes[i];

    const isXCoordinate = isXAxis(axis);
    const numTicks = isXCoordinate ? horizontalNumTicks : verticalNumTicks;
    const scale = getCurrentScale(axisIdToScales, axis.id);

    if (isNumericalAxis(axis)) {
      const formatter = getFormatterByTicks(
        numTicks,
        scale,
        axis.unit,
        axis.formatter,
      );
      axisIdToFormatter.set(axis.id, formatter);
    } else if (isTimeAxis(axis)) {
      const formatter = getTimeFormatter(axis.formatter);
      axisIdToFormatter.set(axis.id, formatter);
    } else {
      const formatter = getFormatterByTicks(numTicks, scale);
      axisIdToFormatter.set(axis.id, formatter);
    }
  }

  return (
    <XYChartAxesFormatterContext.Provider value={axisIdToFormatter}>
      {children}
    </XYChartAxesFormatterContext.Provider>
  );
}
